import React, { useEffect } from "react";

import DateRangePicker from "../../../../../components/dateRangePicker";
import GuestSelector from "../../../../../components/guestSelector";
import DatePicker from "../../../../../components/datePicker";
import Spinner from "../../../../../components/spinner";

import provisionalModuleDataBasedOnLanguage from "../../provisionalModuleDataBasedOnLanguage";
import translations from "../../../supplierPage/provisionalModuleDataBasedOnLanguage";

import { fromYYYYMMDD, addDays } from "../../../../../utils/datefns";
import Icon from "../../../../../components/icon/icon";
import { getLanguageField } from "../../../../../utils/language";


const SearchForm = ({
  arrivalDate = null,
  departureDate = null,
  guests = [],
  onSearch = () => null,
  type = "lodging",
  language,
  availability = false,
  loading = false,
  rangeInterval = [new Date(), new Date().setFullYear(new Date().getFullYear() + 1)],
  maxStay = null,
  minStay = null,
  colors,
  isPackage = false,
  onMonthChange,
  _numberOfRooms,
  multiRoom = false,
  maxRooms = 1,
  guestsTypes = null,
  blackoutPeriods = [],
  weekDaysBlocked = [],
  searchingText,
  maxGuests,
  searchFormTranslations,
  minDate = new Date(),
  messaging = null,
  errorOnSearch = false
}) => {
  const getValidDateInterval = (dateInterval) => {
    const newDateInterval = [];
    if(dateInterval[0] < new Date()) newDateInterval.push(new Date())
    else newDateInterval.push(dateInterval[0]);
    newDateInterval.push(dateInterval[1])
    return newDateInterval;
  }

  const [dateRange, setDateRange] = React.useState([arrivalDate, departureDate]);
  const [_startDate, _endDate] = dateRange;
  const [rangeIntervalState, setRangeIntervalState] = React.useState(getValidDateInterval(rangeInterval))
  const [numberOfRooms, setNumberOfRooms] = React.useState(_numberOfRooms || 1);
  const [_guests, setGuests] = React.useState(guests && guests.length > 0 ? guests : [[{type: "adult", age:undefined}]]);

  useEffect(() => {
    if (arrivalDate !== dateRange[0] || departureDate !== dateRange[1]) {
      setDateRange([arrivalDate, departureDate]);
    }
    if (guests !== _guests && guests && guests.length) {
      setGuests(guests);
    }
    if (_numberOfRooms !== numberOfRooms && _numberOfRooms) {
      setNumberOfRooms(_numberOfRooms)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivalDate, departureDate, guests, _numberOfRooms])

  const disabled = loading || (!(_startDate && _endDate && _guests.length ) && (type === "lodging" || type === "hotel" || type === "unit")) || (!_startDate && (type === "activity" ||type === "restaurant"));

  const moduleData = provisionalModuleDataBasedOnLanguage(language);
  const translationsGuestSelector = translations(language);

  const excludeDates = [];
  if (availability) {
    availability.forEach(date => {
      if (date.available < 1) excludeDates.push(fromYYYYMMDD(date.day))
    })
  }

  const updateDate = (dates) => {
    if(dates[0] && !dates[1]){
      if(maxStay || minStay){
        let minDate = minStay ? addDays(dates[0], minStay) : dates[0];
        let maxDate = maxStay ? addDays(dates[0], maxStay) : null;
        if (rangeIntervalState[0] > minDate) minDate = rangeIntervalState[0];
        if (rangeIntervalState[1] < maxDate) maxDate = rangeIntervalState[1];
        setRangeIntervalState([minDate, maxDate])
      }
    }
    setDateRange(dates)
  }
  const checkForDates = () => {
    if (!dateRange[1]) {
      setDateRange([arrivalDate, departureDate])
      setRangeIntervalState(getValidDateInterval(rangeInterval))
    }
  }

  const onOpen = () => {
    //reset range interval once start and end date selected
    if(_startDate && _endDate) setRangeIntervalState(getValidDateInterval(rangeInterval))
  }

  const updateGuests = (index, guests) => {
    let newGuests = [..._guests];
    newGuests[index] = guests;
    setGuests(newGuests);
  }

  const types = guestsTypes ? [
    {
      type: "adult",
      label: translationsGuestSelector.adultsLabel,
      minRange: guestsTypes.min_adults,
      maxRange: guestsTypes.max_adults,
      minAge: guestsTypes.junior_max_age ? guestsTypes.junior_max_age + 1 : 13,
      maxAge: false
    },
    {
      type: "child",
      label: translationsGuestSelector.childrenLabel,
      minRange: guestsTypes.min_junior,
      maxRange: guestsTypes.max_junior,
      minAge: guestsTypes.junior_min_age ? guestsTypes.junior_min_age : 0,
      maxAge: guestsTypes.junior_max_age ? guestsTypes.junior_max_age : 13,
      ageRequired: true,
      ageRequiredLabel: translationsGuestSelector.ageRequiredLabel,
      ageRequiredCaption: translationsGuestSelector.ageRequiredCaption,
      ageLabel: translationsGuestSelector.ageLabel,
    },
  ] : null;

  const formTitle = (type !== "activity" && type !== "restaurant" && type !== "activities" && type !== "restaurants" && type !== "lift-ticket") ? moduleData.dateFormLabel : moduleData.formTitleActivities;
  const buttonStyles = `flex justify-center mt-4 w-full p-2 ${searchingText && loading ? "" : "uppercase" } text-base rounded ${disabled ? "bg-gray-300 text-white" : "bg-[color:var(--primaryColor)] text-[color:var(--secondaryColor)]"}`

  const isDateInRange = (startDate, endDate, from, to) => {
    if (!startDate && !endDate) return false;
    const messageFrom = new Date(from);
    const messageTo = new Date(to);
    return (startDate && startDate >= messageFrom && startDate <= messageTo) || 
           (endDate && endDate >= messageFrom && endDate <= messageTo);
  };

  return (
    <div className={`mt-auto p-6 ${isPackage ? "" : "bg-[color:var(--highlightedPrimaryColor)]"}  rounded-2xl`} style={{'--highlightedPrimaryColor': colors?.formPrimaryColor || "white", '--highlightedSecondaryColor': colors?.formSecondaryColor}}>
      <div className={"flex flex-col"}>
        <span className={`block font-[800] text-[0.9375rem] leading-[1.175rem] mb-4 ${isPackage ? "" : "text-[color:var(--highlightedSecondaryColor)]"}`}>{formTitle}</span>
        {
          type !== "activity" && type !== "restaurant" && type !== "activities" && type !== "restaurants" && type !== "lift-ticket" ?
          <DateRangePicker
            weekdaysBlocked={weekDaysBlocked.map(day => parseInt(day))}
            destinationColors={!isPackage}
            minDate={rangeIntervalState[0]}
            maxDate={rangeIntervalState[1]}
            openToDate={_startDate}
            availability={availability}
            startDatePlaceholder={moduleData.checkinLabelPlaceholder}
            endDatePlaceholder={moduleData.checkoutLabelPlaceholder}
            excludeDates={excludeDates}
            accentColor={"#6D62E4"}
            accentColorText={"#ffffff"}
            startDate={_startDate}
            endDate={_endDate}
            onOpen={onOpen}
            onChange={updateDate}
            locale={language}
            shouldCloseOnSelect={true}
            onMonthChange={onMonthChange}
            excludeDateIntervals={blackoutPeriods && blackoutPeriods.length && blackoutPeriods.map(bp => { return { start: new Date(bp.From).setHours(0, 0, 0, 0), end: new Date(bp.To) }})}
            onClose={checkForDates}
          />
        :
          <DatePicker
            destinationColors={!isPackage}
            years={[new Date().getFullYear(), new Date().getFullYear() + 1]}
            value={_startDate}
            label={moduleData.datePickerPlaceholderStart}
            includeDateIntervals={null}
            onChange={(date) => {
              setDateRange([date, departureDate])
            }}
            locale={language}
            minDate={minDate}
            maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
            weekdaysBlocked={weekDaysBlocked.map(day => parseInt(day))}
            excludeDateIntervals={blackoutPeriods && blackoutPeriods.length && blackoutPeriods.map(bp => { return { start: new Date(bp.From).setHours(0, 0, 0, 0), end: new Date(bp.To) }})}
          />
        }
        {/* Show informative messages */}
        {messaging?.map((msg, index) => {
          if (msg.search_form_messaging_id.type === "informative" && 
              isDateInRange(arrivalDate, departureDate, msg.search_form_messaging_id.from, msg.search_form_messaging_id.to)) {
            return (
              <div key={index} className="mt-4">
                {getLanguageField(msg.search_form_messaging_id.translations, language, "message")}
              </div>
            );
          }
          return null;
        })}
        {
          type !== "activity" && type !== "restaurant" && type !== "activities" && type !== "restaurants" && type !== "lift-ticket" ?
          <>
            <span className={`block font-[800] text-[0.9375rem] leading-[1.175rem] mt-6 mb-4 ${!isPackage ? "text-[color:var(--highlightedSecondaryColor)]" : ""}`}>{moduleData.paxFormLabel}</span>
            {
              Array.from({length: numberOfRooms}, (_, index) => {
                const isLast = index === numberOfRooms -1;
                return (
                  <>
                    {numberOfRooms > 1 && <div className="flex gap-2 items-center justify-between mb-4">
                        <span className="font-[700]">{moduleData.room} {index + 1}</span>
                        {
                          isLast ?
                            <button className="flex border rounded-lg p-2" onClick={() => {
                              setNumberOfRooms(numberOfRooms - 1);
                              const newGuests = [..._guests];
                              newGuests.splice(_guests.length - 1);
                              setGuests(newGuests);
                            }}>
                              <Icon icon="X"/>
                            </button>
                          : null
                        }
                      </div>
                    }
                    <div key={`selector_${index}`} className={`mb-4 border-2 ${!isPackage ? "border-[color:var(--highlightedSecondaryColor)]" : "border[#EAEAF2]"} p-4 rounded-2xl`}>
                      <GuestSelector
                        types={types}
                        destinationColors={!isPackage}
                        language={language}
                        accentColor={"#6D62E4"}
                        guests={_guests[index]}
                        maxGuests={maxGuests}
                        setGuests={(guests) => updateGuests(index, guests)}
                      />
                    </div>
                    {
                      maxGuests !== 0 && maxGuests === _guests[index].length 
                      ? <span className="font-normal text-sm">{getLanguageField(searchFormTranslations, language, "maximum_guest_capacity")}</span>
                      : null
                    }
                  </>
                )
              })
            }

            {
              multiRoom && numberOfRooms <= maxRooms
              ? <button
                  onClick={() => {
                    setNumberOfRooms(numberOfRooms + 1);
                    const newGuests = [..._guests];
                    newGuests.push([{type: "adult", age: undefined}]);
                    setGuests(newGuests);
                  }}
                  className={`flex items-center gap-2 mt-4 font-[800] underline text-[color:var(--primaryColor)]`}
                >
                  <Icon icon="Plus" />
                  <span>{moduleData.addRoom}</span>
                </button>
              : null
            }
          </>
          : null
        }
      </div>
      <button disabled={disabled} onClick={() => onSearch(_startDate, _endDate, _guests, numberOfRooms)} className={buttonStyles}>
        {
          loading
          ? searchingText || <Spinner />
          : moduleData.searchButtonLabel
        }
      </button>
      {/* Show error messages */}
      {errorOnSearch ? messaging?.map((msg, index) => {
        if (msg.search_form_messaging_id.type === "error" && 
            isDateInRange(arrivalDate, departureDate, msg.search_form_messaging_id.from, msg.search_form_messaging_id.to)) {
          return (
            <div key={index} className="rounded-lg bg-[#FDEDED] text-[#E10003] p-4 font-[600] mt-4">
              {getLanguageField(msg.search_form_messaging_id.translations, language, "message")}
            </div>
          );
        }
        return null;
      }) : null}
    </div>
  )
}

export default SearchForm;